// Sprints (Deborah's Q4 plan, 1 Nov: weekly vertical sprints). A sprint is a checked-in account
// pool with `kind: 'sprint'` (agent_api/pools.py), joined on serve into `row.pools`, so there is no
// second tag: All accounts' Sprint / pool column and both tabs' Sprint and Pool filters all read
// the same memberships.
//
// Pure, and imported by `node --test`.

import type { AccountPool, BookAccount } from '../api/types';

export function isSprint(pool: AccountPool): boolean {
  return pool.kind === 'sprint';
}

// The sprints a row is in, in the order the server joined them.
export function sprintsOf(row: BookAccount): AccountPool[] {
  return (row.pools ?? []).filter(isSprint);
}

// A row's sprints as text: each sprint's label, joined.
export function sprintLabelText(row: BookAccount): string {
  return sprintsOf(row).map(sprint => sprint.label).join(' · ');
}

export function hasSprint(rows: BookAccount[]): boolean {
  return rows.some(row => sprintsOf(row).length > 0);
}

// --- the Sprint and Pool filters, the same on both tabs -------------------------------------------
//
// My Accounts (`filters.ts`) and All accounts (`allAccountsFilters.ts`) both offer a Sprint filter
// and a Pool filter. Both value an option by the pool's key and match it the same way, so a key
// copied from one tab means the same rows on the other. A sprint is offered under Sprint only, and
// every other pool (the November pool, Nov T100) under Pool only.

export interface PoolChoice {
  key: string;
  label: string;
  // How many of the rows passed in hold it.
  count: number;
}

function choicesWhere(rows: BookAccount[], wanted: (pool: AccountPool) => boolean): PoolChoice[] {
  const byKey = new Map<string, PoolChoice>();
  for (const row of rows) {
    for (const pool of row.pools ?? []) {
      if (!wanted(pool)) continue;
      const choice = byKey.get(pool.key);
      if (choice) choice.count += 1;
      else byKey.set(pool.key, { key: pool.key, label: pool.label, count: 1 });
    }
  }
  return [...byKey.values()];
}

function isPlainPool(pool: AccountPool): boolean {
  return !isSprint(pool);
}

// The sprints on the rows, in the order first seen, each with its row count.
export function sprintChoices(rows: BookAccount[]): PoolChoice[] {
  return choicesWhere(rows, isSprint);
}

// The other pools on the rows, the same way.
export function poolChoices(rows: BookAccount[]): PoolChoice[] {
  return choicesWhere(rows, isPlainPool);
}

export function inSprint(row: BookAccount, sprintKey: string): boolean {
  return sprintsOf(row).some(sprint => sprint.key === sprintKey);
}

export function inPool(row: BookAccount, poolKey: string): boolean {
  return (row.pools ?? []).some(pool => pool.key === poolKey && isPlainPool(pool));
}

export function sprintKeySet(rows: BookAccount[]): Set<string> {
  return new Set(sprintChoices(rows).map(choice => choice.key));
}

// "2–8 Nov", "30 Oct onwards": a sprint's week, short. '' when it has no start.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayAndMonth(day: string): { day: number; month: string } | null {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  return { day: Number(match[2]), month: MONTHS[Number(match[1]) - 1] ?? '' };
}

export function sprintWeekText(sprint: Pick<AccountPool, 'starts_on' | 'ends_on'>): string {
  const start = dayAndMonth(sprint.starts_on ?? '');
  if (!start) return '';
  const end = dayAndMonth(sprint.ends_on ?? '');
  if (!end) return `${start.day} ${start.month} onwards`;
  if (end.month === start.month) return `${start.day}–${end.day} ${end.month}`;
  return `${start.day} ${start.month} – ${end.day} ${end.month}`;
}
